import React, { CSSProperties, useEffect } from 'react';
import { useField } from 'formik';
import { useSmartFormContext } from '../forms';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import InputBase from '@material-ui/core/InputBase';
import { Checkbox, Typography } from '@utmost.co/utmost-design-system';
import { ComponentPropBase } from '../types';
import { PermissionHelper } from '../atoms';
import FormHelperText from '@material-ui/core/FormHelperText';

interface CheckboxProps extends ComponentPropBase {
  style?: CSSProperties;
  label?: string;
  fullWidth?: boolean;
  helpText?: string;
  disabled?: boolean;
  initialValue?: boolean;
  onChange?: (value: boolean) => void;
}

export const CheckboxField = ({
  name,
  disabled,
  initialValue,
  label,
  helpText,
  onChange,
  fullWidth,
  required,
  infoText,
  touched,
  helpTextBelow,
  errors,
  error,
  style,
  ...props
}: CheckboxProps) => {
  const [input, meta, helpers] = useField(name || 'INVALID');
  const { getDisabled, readonly, setFieldValue, getFieldPermissions, permissionDebugger, submitCount } =
    useSmartFormContext();
  const permissions = getFieldPermissions(name);
  if (getDisabled(name) || readonly) {
    disabled = true;
  }

  const handleChange = (value: boolean) => {
    onChange ? onChange(value) : setFieldValue(name, value);
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    if (typeof initialValue === 'boolean') {
      helpers.setValue(initialValue);
    } else if (initialValue === 'true' || initialValue === 'false') {
      helpers.setValue(initialValue === 'true');
    }
  }, []);

  if (readonly) {
    return (
      <FormControl disabled data-test={`${name}-field-control`} id={name}>
        <FormLabel>{label}</FormLabel>
        <InputBase value={input.value ? 'Yes' : 'No'} />
      </FormControl>
    );
  }
  const id = `${name}-checkbox`;
  const isError = (!!meta.error || (error && !!errors)) && submitCount > 0;
  return (
    <FormControl error={!!meta.error && !!meta.touched} required={required} fullWidth={fullWidth}>
      <Checkbox
        checked={input.value}
        required={required}
        onChange={() => handleChange(!input.value)}
        id={id}
        dataTest={`${name}-checkbox`}
        disabled={disabled}
        subLabel={helpText}
        error={isError}
        {...props}
      >
        <Typography variant="label1">{label}</Typography>
      </Checkbox>
      {(!!meta.error || (error && !!errors)) && submitCount > 0 && (
        <FormHelperText error>{meta.error ?? errors}</FormHelperText>
      )}
      {permissionDebugger && <PermissionHelper permission={permissions} />}
    </FormControl>
  );
};
